import type { CSSProperties, ReactNode } from 'react';
import { ChevronsDown } from 'lucide-react';
import { useBodyClass } from '../../lib/body-class';
import Breadcrumbs, { type BreadcrumbItem } from '../Breadcrumbs';
import Button from './Button';
import CurveDivider from './CurveDivider';

/**
 * C2 photo hero: full-bleed <img> (LCP: eager + fetchpriority high), neutral
 * scrim, white H1, optional subhead, two outline-light buttons (second hidden
 * on phones), double-chevron scroll cue and the curve. `size="home"` centres
 * the content; destination pages use `align="left"` inside the container.
 * On desktop the photo runs under the frosted header (body.ve-has-photo-hero).
 *
 *   <PhotoHero size="home" title="..." image={{ src, alt, width: 1600, height: 900, focal: '50% 35%' }}
 *     primary={{ label: 'Book Online', to: SITE.booking.chooserPath }} secondary={{ label: 'Meet the Doctors', to: '/meet-the-doctors/' }} />
 */
export interface HeroImage {
  src: string;
  alt: string;
  srcSet?: string;
  sizes?: string;
  width?: number;
  height?: number;
  /** object-position for the focal point, e.g. "50% 30%". */
  focal?: string;
  /** object-position below 768px, where only a narrow slice of a landscape photo shows. */
  focalMobile?: string;
  /**
   * Art direction: extra crops for wider screens, rendered as <source media>
   * inside a <picture>. The <img> itself carries the PHONE crop, so the
   * prerendered hero preload (which reads the <img>) fetches the right file
   * on mobile, where Lighthouse and most visitors are.
   */
  sources?: Array<{ media: string; srcSet: string; sizes?: string; width?: number; height?: number }>;
}

export interface HeroAction {
  label: string;
  href?: string;
  to?: string;
  external?: boolean;
}

export interface PhotoHeroProps {
  title: ReactNode;
  image: HeroImage;
  eyebrow?: ReactNode;
  subtitle?: ReactNode;
  primary?: HeroAction;
  secondary?: HeroAction;
  /** Replaces primary/secondary with custom content. */
  actions?: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  size?: 'home' | 'page';
  align?: 'center' | 'left';
  /** Element id the scroll cue links to (default "main"). Pass null to hide the cue. */
  scrollTargetId?: string | null;
  /** Override the scrim (see --wash-hero); e.g. a stronger one for a bright photo. */
  scrim?: string;
  titleId?: string;
  className?: string;
  children?: ReactNode;
}

const BODY_CLASSES = ['ve-has-photo-hero'];

export default function PhotoHero({
  title,
  image,
  eyebrow,
  subtitle,
  primary,
  secondary,
  actions,
  breadcrumbs,
  size = 'page',
  align = size === 'home' ? 'center' : 'left',
  scrollTargetId = 'main',
  scrim,
  titleId,
  className = '',
  children,
}: PhotoHeroProps) {
  useBodyClass(BODY_CLASSES);

  const sectionCls = ['ve-photo-hero', `ve-photo-hero--${size}`, className].filter(Boolean).join(' ');
  const contentCls = ['ve-container', 've-photo-hero-content', align === 'left' ? 've-photo-hero-content--left' : ''].filter(Boolean).join(' ');
  const imgStyle =
    image.focal || image.focalMobile
      ? ({ '--focal': image.focal, '--focal-mobile': image.focalMobile } as CSSProperties)
      : undefined;

  const heroImg = (
    <img
      className="ve-photo-hero-img"
      src={image.src}
      srcSet={image.srcSet}
      sizes={image.sizes ?? '100vw'}
      alt={image.alt}
      width={image.width}
      height={image.height}
      loading="eager"
      decoding="async"
      style={imgStyle}
      // React 18 has no typed fetchPriority; the lowercase attribute passes through untouched.
      {...({ fetchpriority: 'high' } as Record<string, string>)}
    />
  );

  return (
    <section className={sectionCls} aria-labelledby={titleId}>
      {image.sources && image.sources.length > 0 ? (
        <picture className="ve-photo-hero-picture">
          {image.sources.map((src) => (
            <source key={src.media} media={src.media} srcSet={src.srcSet} sizes={src.sizes ?? '100vw'} width={src.width} height={src.height} />
          ))}
          {heroImg}
        </picture>
      ) : (
        heroImg
      )}
      <div className="ve-photo-hero-scrim" aria-hidden="true" style={scrim ? { background: scrim } : undefined} />
      <div className={contentCls}>
        {breadcrumbs && breadcrumbs.length > 0 && <Breadcrumbs items={breadcrumbs} />}
        {eyebrow && <span className="ve-eyebrow">{eyebrow}</span>}
        <h1 className="ve-photo-hero-title" id={titleId}>
          {title}
        </h1>
        {subtitle && <p className="ve-photo-hero-sub">{subtitle}</p>}
        {actions ??
          ((primary || secondary) && (
            <div className="ve-btn-row ve-photo-hero-actions">
              {primary && (
                <Button variant="outline-light" size="lg" href={primary.href} to={primary.to} external={primary.external}>
                  {primary.label}
                </Button>
              )}
              {secondary && (
                <Button variant="outline-light" size="lg" href={secondary.href} to={secondary.to} external={secondary.external}>
                  {secondary.label}
                </Button>
              )}
            </div>
          ))}
        {children}
      </div>
      {scrollTargetId && (
        <a className="ve-scroll-cue" href={`#${scrollTargetId}`} aria-label="Scroll to the page content">
          <ChevronsDown aria-hidden="true" focusable="false" />
        </a>
      )}
      <CurveDivider />
    </section>
  );
}
